import validate from '@/manual-review/validations';
import * as mxValidations from '@/documents-validation/mx_national_id';
import { FieldType } from '@/manual-review/form';
import { Document, CountryAlpha3 } from './types';

const foreignIDRegexp = /^[0-9]{10,13}$/;
const expectedMRZLength = 90;
const td1MRZRegexp = /^[A-Z0-9<]+$/;
const cicNumberRegexp = /^[0-9]{8}$/;

export function validateDocumentNumberWithPossibleHint(value: string): string | boolean {
  if (!foreignIDRegexp.test(value)) {
    return 'Digite 10 a 13 dígitos';
  }

  return true;
}

export function validateMRZ(value: string): string | boolean {
  if (value.length !== expectedMRZLength) {
    return `Digite un MRZ de ${expectedMRZLength} caracteres`;
  }

  if (!td1MRZRegexp.test(value)) {
    return 'Digite un MRZ con caracteres válidos';
  }

  return true;
}

export function validateCICNumber(value: string): string | boolean {
  if (!cicNumberRegexp.test(value)) {
    return 'Digite un CIC válido de 8 dígitos';
  }

  return true;
}

const document: Document = {
  sides: {
    front: {
      fields: [
        {
          type: FieldType.Select,
          name: 'document_version',
          label: 'Versión de documento',
          rules: [validate.isRequired],
          value: 'mx_fm3',
          options: [
            { label: 'FM2', value: 'mx_fm2' },
            { label: 'FM3', value: 'mx_fm3' },
          ],
          alwaysRequired: true,
        },
        {
          type: FieldType.Text,
          name: 'name',
          label: 'Nombres',
          rules: [validate.isRequired, validate.namesWithSpecialCharacters],
          value: '',
        },
        {
          type: FieldType.Text,
          name: 'last_name',
          label: 'Apellidos',
          rules: [validate.isRequired, validate.namesWithSpecialCharacters],
          value: '',
        },
        {
          type: FieldType.Text,
          name: 'foreign_id',
          label: 'NUE',
          rules: [validate.isRequired, validateDocumentNumberWithPossibleHint],
          value: '',
        },
        {
          type: FieldType.Text,
          name: 'nationality',
          label: 'Nacionalidad',
          rules: [validate.isRequired, validate.name],
          value: '',
        },
        {
          type: FieldType.Date,
          name: 'date_of_birth',
          label: 'Fecha de nacimiento',
          rules: [validate.isRequired, validate.isDate, validate.dateOfBirth],
          allowedDates: validate.dateOfBirth,
          value: '',
        },
        {
          type: FieldType.Select,
          name: 'gender',
          label: 'Sexo',
          rules: [validate.isRequired],
          value: '',
          options: [
            { label: 'Hombre', value: 'male' },
            { label: 'Mujer', value: 'female' },
          ],
        },
        {
          type: FieldType.Date,
          name: 'expiration_date',
          label: 'Fecha de vencimiento',
          rules: [validate.isDate, validate.dateOfExpiration],
          allowedDates: validate.dateOfExpiration,
          value: '',
        },
        {
          type: FieldType.Text,
          name: 'document_number',
          label: 'CURP',
          rules: [validate.isRequired, mxValidations.validateDocumentNumber],
          value: '',
        },
      ],
    },
    reverse: {
      fields: [
        {
          type: FieldType.Td1MRZ,
          name: 'machine_readable',
          label: 'Machine-Readable Zone (TD1)',
          rules: [validateMRZ],
          value: '',
          td1MRZFields: {
            issuingCountry: {
              type: FieldType.Select,
              name: 'mrz_issuing_country',
              label: 'MRZ issuing country',
              rules: [validate.isRequired],
              value: '',
              options: CountryAlpha3,
            },
            documentNumber: {
              type: FieldType.Text,
              name: 'mrz_document_number',
              label: 'MRZ CIC',
              rules: [validate.isRequired, validateCICNumber],
              value: '',
            },
            dateOfBirth: {
              type: FieldType.Date,
              name: 'mrz_date_of_birth',
              label: 'MRZ date of birth',
              rules: [validate.isDate, validate.dateOfBirth],
              allowedDates: validate.dateOfBirth,
              value: '',
            },
            gender: {
              type: FieldType.Select,
              name: 'mrz_gender',
              label: 'MRZ gender',
              rules: [validate.isRequired],
              value: '',
              options: [
                { label: 'M', value: 'M' },
                { label: 'F', value: 'F' },
              ],
            },
            expirationDate: {
              type: FieldType.Date,
              name: 'mrz_expiration_date',
              label: 'MRZ expiration date',
              rules: [validate.isDate, validate.dateOfExpiration],
              allowedDates: validate.dateOfExpiration,
              value: '',
            },
            nationality: {
              type: FieldType.Select,
              name: 'mrz_nationality',
              label: 'MRZ nationality',
              rules: [validate.isRequired],
              value: '',
              options: CountryAlpha3,
            },
            lastName: {
              type: FieldType.Text,
              name: 'mrz_last_name',
              label: 'MRZ last name',
              rules: [validate.name],
              value: '',
            },
            name: {
              type: FieldType.Text,
              name: 'mrz_name',
              label: 'MRZ name',
              rules: [validate.name],
              value: '',
            },
          },
        },
      ],
    },
  },
};

export default document;
